/*
 * Hosting / VPS COGS & capacity engine.
 */

export const DEFAULT_LOCATION = {
  colocationMode: "per_u",
  colocationPerU: 1500000,
  rackMonthly: 15000000,
  serversPerRack: 10,
  powerMode: "included",
  tariffKwh: 1700,
  pue: 1.5,
  bandwidthMbps: 100,
  bandwidthPricePerMbps: 50000,
  ipPricePerMonth: 15000,
  otherMonthly: 0,
};

export const DEFAULT_PARAMS = {
  cpuOvercommit: 2,
  ramOvercommit: 1,
  diskOvercommit: 1,
  cpuReservePct: 10,
  ramReservePct: 10,
  diskReservePct: 10,
  targetUtilPct: 80,
  wCpu: 40,
  wRam: 40,
  wDisk: 20,
  marginPct: 50,
  maintenancePctYear: 5,
  staffPerServer: 0,
  spreadSupportServers: true,
};

export const MARKET_VPS = [
  { name: "VPS Nano", vcpu: 1, ramGb: 1, diskGb: 25, bandwidthTb: 1 },
  { name: "VPS Micro", vcpu: 1, ramGb: 2, diskGb: 50, bandwidthTb: 2 },
  { name: "VPS Small", vcpu: 2, ramGb: 4, diskGb: 80, bandwidthTb: 3 },
  { name: "VPS Medium", vcpu: 4, ramGb: 8, diskGb: 160, bandwidthTb: 4 },
  { name: "VPS Large", vcpu: 8, ramGb: 16, diskGb: 320, bandwidthTb: 5 },
  { name: "VPS XLarge", vcpu: 16, ramGb: 32, diskGb: 640, bandwidthTb: 6 },
];

export const MARKET_SHARED = [
  { name: "Hosting Personal", diskGb: 2, accountsPerVcpu: 30, accountsPerGbRam: 15, diskUsagePct: 35 },
  { name: "Hosting Starter", diskGb: 5, accountsPerVcpu: 25, accountsPerGbRam: 12, diskUsagePct: 35 },
  { name: "Hosting Business", diskGb: 20, accountsPerVcpu: 15, accountsPerGbRam: 8, diskUsagePct: 35 },
  { name: "Hosting Pro", diskGb: 50, accountsPerVcpu: 10, accountsPerGbRam: 5, diskUsagePct: 35 },
];

let _pid = 0;
export const newPackage = (over = {}) => ({
  id: `pkg-${Date.now().toString(36)}-${(_pid++).toString(36)}`,
  name: "Paket Baru",
  type: "vps",
  vcpu: 2,
  ramGb: 4,
  diskGb: 80,
  bandwidthTb: 3,
  ipv4: 1,
  dedicatedMbps: 0,
  licenseMonthly: 0,
  supportMonthly: 0,
  price: 0,
  accountsPerVcpu: 25,
  accountsPerGbRam: 12,
  diskUsagePct: 35,
  ...over,
});

export const HOSTING_DEFAULT = () => ({
  tab: "inventory",
  selectedServerIds: null, // null => all compute + support servers
  basis: "pool",
  params: { ...DEFAULT_PARAMS },
  locations: {},
  packages: [
    newPackage({ name: "VPS Small", vcpu: 2, ramGb: 4, diskGb: 80, bandwidthTb: 3 }),
    newPackage({ name: "Hosting Business", type: "shared", vcpu: 0, ramGb: 0, diskGb: 20, ipv4: 0, accountsPerVcpu: 15, accountsPerGbRam: 8 }),
  ],
  capacityPackageId: null,
});

const num = (x) => (Number.isFinite(+x) ? +x : 0);

export function serverCapacity(s, p) {
  const vcpu = num(s.threads || s.cores) * (1 - num(p.cpuReservePct) / 100) * num(p.cpuOvercommit || 1);
  const ram = num(s.ram_gb) * (1 - num(p.ramReservePct) / 100) * num(p.ramOvercommit || 1);
  const disk = num(s.storage_tb) * 1000 * (1 - num(p.diskReservePct) / 100) * num(p.diskOvercommit || 1);
  return { vcpu, ram, disk };
}

export function locationOf(state, name) {
  return { ...DEFAULT_LOCATION, ...(state.locations?.[name] || {}) };
}

export function serverMonthlyCost(s, loc, p, nInLocation) {
  const price = num(s.purchase_price);
  const depreciation = price / Math.max(1, num(s.depreciation_months) || 48);
  const maintenance = (price * num(p.maintenancePctYear)) / 100 / 12;
  const colocation =
    loc.colocationMode === "per_rack"
      ? num(loc.rackMonthly) / Math.max(1, num(loc.serversPerRack))
      : num(s.rack_units) * num(loc.colocationPerU);
  const power = loc.powerMode === "metered" ? (num(s.power_watt) / 1000) * 730 * num(loc.tariffKwh) * num(loc.pue || 1) : 0;
  const bandwidth = (num(loc.bandwidthMbps) * num(loc.bandwidthPricePerMbps) + num(loc.otherMonthly)) / Math.max(1, nInLocation);
  const ip = num(s.ip_count) * num(loc.ipPricePerMonth);
  const license = num(s.license_monthly);
  const staff = num(p.staffPerServer);
  const total = depreciation + maintenance + colocation + power + bandwidth + ip + license + staff;
  return { depreciation, maintenance, colocation, power, bandwidth, ip, license, staff, total };
}

function unitCosts(totalCost, cap, p) {
  const util = Math.max(0.05, num(p.targetUtilPct) / 100);
  const wsum = num(p.wCpu) + num(p.wRam) + num(p.wDisk) || 1;
  return {
    cpu: cap.vcpu > 0 ? (totalCost * (num(p.wCpu) / wsum)) / (cap.vcpu * util) : 0,
    ram: cap.ram > 0 ? (totalCost * (num(p.wRam) / wsum)) / (cap.ram * util) : 0,
    disk: cap.disk > 0 ? (totalCost * (num(p.wDisk) / wsum)) / (cap.disk * util) : 0,
  };
}

export function computeFleet(servers, state) {
  const p = { ...DEFAULT_PARAMS, ...(state.params || {}) };
  const selectedIds = state.selectedServerIds;
  const selected = servers.filter((s) => (selectedIds ? selectedIds.includes(s.id) : true));
  const byLoc = {};
  selected.forEach((s) => {
    byLoc[s.location] = (byLoc[s.location] || 0) + 1;
  });
  const rows = selected.map((s) => {
    const loc = locationOf(state, s.location);
    const cost = serverMonthlyCost(s, loc, p, byLoc[s.location] || 1);
    const cap = serverCapacity(s, p);
    return { server: s, loc, cost, cap };
  });
  const compute = rows.filter((r) => r.server.role === "compute");
  const support = rows.filter((r) => r.server.role !== "compute");
  const supportCost = support.reduce((a, r) => a + r.cost.total, 0);
  const supportShare = p.spreadSupportServers && compute.length ? supportCost / compute.length : 0;
  compute.forEach((r) => {
    r.supportShare = supportShare;
    r.totalCost = r.cost.total + supportShare;
    r.unit = unitCosts(r.totalCost, r.cap, p);
    const a = r.server.allocated || { vcpu: 0, ram_gb: 0, disk_gb: 0, count: 0 };
    r.alloc = a;
    r.remaining = { vcpu: r.cap.vcpu - a.vcpu, ram: r.cap.ram - a.ram_gb, disk: r.cap.disk - a.disk_gb };
  });
  support.forEach((r) => {
    r.totalCost = r.cost.total;
  });
  const poolCap = compute.reduce(
    (a, r) => ({ vcpu: a.vcpu + r.cap.vcpu, ram: a.ram + r.cap.ram, disk: a.disk + r.cap.disk }),
    { vcpu: 0, ram: 0, disk: 0 }
  );
  const poolCost = compute.reduce((a, r) => a + r.totalCost, 0);
  const poolUnit = unitCosts(poolCost, poolCap, p);
  const nC = Math.max(1, compute.length);
  const avgCap = { vcpu: poolCap.vcpu / nC, ram: poolCap.ram / nC, disk: poolCap.disk / nC };
  const avgCost = poolCost / nC;
  const ipPrices = compute.map((r) => num(r.loc.ipPricePerMonth));
  const bwPrices = compute.map((r) => num(r.loc.bandwidthPricePerMbps));
  const avg = (arr) => (arr.length ? arr.reduce((a, b) => a + b, 0) / arr.length : 0);
  const poolAlloc = compute.reduce(
    (a, r) => ({ vcpu: a.vcpu + r.alloc.vcpu, ram: a.ram + r.alloc.ram_gb, disk: a.disk + r.alloc.disk_gb, count: a.count + r.alloc.count }),
    { vcpu: 0, ram: 0, disk: 0, count: 0 }
  );
  const missingPrice = selected.filter((s) => !num(s.purchase_price)).length;
  return {
    params: p,
    rows,
    compute,
    support,
    supportCost,
    supportShare,
    pool: {
      cap: poolCap,
      cost: poolCost,
      unit: poolUnit,
      avgCap,
      avgCost,
      ipPrice: avg(ipPrices),
      bwPrice: avg(bwPrices),
      alloc: poolAlloc,
      remaining: { vcpu: poolCap.vcpu - poolAlloc.vcpu, ram: poolCap.ram - poolAlloc.ram, disk: poolCap.disk - poolAlloc.disk },
    },
    totalMonthly: rows.reduce((a, r) => a + r.cost.total, 0),
    missingPrice,
    selectedCount: selected.length,
  };
}

export function basisOf(fleet, basis) {
  if (basis && basis !== "pool") {
    const r = fleet.compute.find((x) => x.server.id === basis);
    if (r)
      return {
        label: r.server.hostname,
        unit: r.unit,
        cap: r.cap,
        serverCost: r.totalCost,
        ipPrice: num(r.loc.ipPricePerMonth),
        bwPrice: num(r.loc.bandwidthPricePerMbps),
      };
  }
  return {
    label: "Rata-rata pool",
    unit: fleet.pool.unit,
    cap: fleet.pool.avgCap,
    serverCost: fleet.pool.avgCost,
    ipPrice: fleet.pool.ipPrice,
    bwPrice: fleet.pool.bwPrice,
  };
}

const ceilTo = (x, step) => Math.ceil(x / step) * step;

export function evaluatePackage(pkg, b, p) {
  const util = Math.max(0.05, num(p.targetUtilPct) / 100);
  const margin = Math.min(0.95, Math.max(0, num(p.marginPct) / 100));
  let resource = 0;
  let users = 0;
  let bottleneck = "-";
  const parts = {};
  if (pkg.type === "dedicated") {
    parts.server = b.serverCost;
    resource = b.serverCost;
    users = 1;
    bottleneck = "Server penuh";
  } else if (pkg.type === "shared") {
    const accV = Math.max(1, num(pkg.accountsPerVcpu));
    const accR = Math.max(1, num(pkg.accountsPerGbRam));
    const diskUsed = num(pkg.diskGb) * (num(pkg.diskUsagePct) / 100);
    parts.cpu = b.unit.cpu / accV;
    parts.ram = b.unit.ram / accR;
    parts.disk = diskUsed * b.unit.disk;
    resource = parts.cpu + parts.ram + parts.disk;
    const lim = [
      ["vCPU", b.cap.vcpu * accV],
      ["RAM", b.cap.ram * accR],
      ["Disk", diskUsed > 0 ? b.cap.disk / diskUsed : Infinity],
    ];
    lim.sort((a, c) => a[1] - c[1]);
    users = Math.floor(lim[0][1]);
    bottleneck = lim[0][0];
  } else {
    parts.cpu = num(pkg.vcpu) * b.unit.cpu;
    parts.ram = num(pkg.ramGb) * b.unit.ram;
    parts.disk = num(pkg.diskGb) * b.unit.disk;
    resource = parts.cpu + parts.ram + parts.disk;
    const lim = [
      ["vCPU", num(pkg.vcpu) > 0 ? b.cap.vcpu / num(pkg.vcpu) : Infinity],
      ["RAM", num(pkg.ramGb) > 0 ? b.cap.ram / num(pkg.ramGb) : Infinity],
      ["Disk", num(pkg.diskGb) > 0 ? b.cap.disk / num(pkg.diskGb) : Infinity],
    ];
    lim.sort((a, c) => a[1] - c[1]);
    users = Number.isFinite(lim[0][1]) ? Math.floor(lim[0][1]) : 0;
    bottleneck = lim[0][0];
  }
  parts.ip = num(pkg.ipv4) * b.ipPrice;
  parts.bandwidth = num(pkg.dedicatedMbps) * b.bwPrice;
  parts.license = num(pkg.licenseMonthly);
  parts.support = num(pkg.supportMonthly);
  const cogs = resource + parts.ip + parts.bandwidth + parts.license + parts.support;
  const suggested = cogs > 0 ? ceilTo(cogs / (1 - margin), 1000) : 0;
  const price = num(pkg.price) > 0 ? num(pkg.price) : suggested;
  const marginActual = price > 0 ? ((price - cogs) / price) * 100 : 0;
  const usersAtTarget = pkg.type === "dedicated" ? 1 : Math.floor(users * util);
  const revenuePerServer = usersAtTarget * price;
  const extrasPerServer = usersAtTarget * (parts.ip + parts.bandwidth + parts.license + parts.support);
  const profitPerServer = revenuePerServer - b.serverCost - extrasPerServer;
  return { cogs, parts, suggested, price, marginActual, users, usersAtTarget, bottleneck, revenuePerServer, profitPerServer };
}

const niceRam = (x) => {
  if (x < 1) return Math.max(0.5, Math.round(x * 2) / 2);
  if (x < 8) return Math.round(x);
  return Math.round(x / 2) * 2;
};
const niceDisk = (x) => (x < 50 ? Math.max(10, Math.round(x / 5) * 5) : Math.round(x / 10) * 10);

export function balancedTiers(cap) {
  if (!cap.vcpu) return [];
  const rp = cap.ram / cap.vcpu;
  const dp = cap.disk / cap.vcpu;
  const names = ["Balanced XS", "Balanced S", "Balanced M", "Balanced L", "Balanced XL"];
  return [1, 2, 4, 8, 16].map((v, i) => ({ name: names[i], vcpu: v, ramGb: niceRam(v * rp), diskGb: niceDisk(v * dp), bandwidthTb: Math.min(8, 1 + i * 1.5) }));
}

export function fitCount(remaining, pkg) {
  if (pkg.type === "dedicated") return remaining.vcpu > 0 ? 0 : 0;
  if (pkg.type === "shared") {
    const diskUsed = num(pkg.diskGb) * (num(pkg.diskUsagePct) / 100);
    return Math.max(
      0,
      Math.floor(
        Math.min(
          remaining.vcpu * num(pkg.accountsPerVcpu),
          remaining.ram * num(pkg.accountsPerGbRam),
          diskUsed > 0 ? remaining.disk / diskUsed : Infinity
        )
      )
    );
  }
  const lims = [];
  if (num(pkg.vcpu) > 0) lims.push(remaining.vcpu / num(pkg.vcpu));
  if (num(pkg.ramGb) > 0) lims.push(remaining.ram / num(pkg.ramGb));
  if (num(pkg.diskGb) > 0) lims.push(remaining.disk / num(pkg.diskGb));
  if (!lims.length) return 0;
  return Math.max(0, Math.floor(Math.min(...lims)));
}
